/*
 * Fund user selection component
 */

import React from 'react';
import Base from './base.jsx';
import {castDataType, DataType, Mode} from '../common.jsx';
import Helpers from './helpers.jsx'
import Setting from './setting.jsx';
import {update} from '../../../helpers/index.js';
import Select, {ReadOnlyMultiSelectValues} from '../../common/Select.jsx';
import UserSelect from '../../common/UserSelect/UserSelect.jsx';
import UserTreeSelect from '../../common/UserSelect/UserTreeSelect.jsx';
import UserOrganisationTreePicker from '../../common/UserSelect/UserOrganisationTreePicker.jsx';

import {
  common_user_type_options_path,
} from '~/routes';

const selectionMethods = [
  {label: I18n.t('dropdown', {scope: 'fe'}), key: 'dropdown'},
  {label: I18n.t('modal', {scope: 'fe'}), key: 'modal'},
]

export default class FundUserAsyncSelect extends Base {

  static get CATEGORY() {
    return 'question';
  }

  static get TYPE() {
    return 'fund_user_async_select';
  }

  static icon() {
    return <i className='fa fa-user'></i>;
  }

  static get TRANSLATED_ATTRIBUTES() {
    return {
      label: 'field_default_label',
      placeholder: 'field_default_placeholder',
      description: 'field_default_description',
      help: null
    };
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      label: 'label',
      placeholder: 'placeholder',
      description: 'description',
      name: 'field_' + Helpers.randomString(),
      reportKey: '',
      help: 'help',
      required: false,
      label_hidden: false,
      useOrganisationTreePicker: false,
      excludeUserTypes: [],
      prettyView: {
        hide: false,
        show_label: false
      }
    });
  }

  constructor(props) {
    super(props);
    this.state = {user_type_options: []};
  }

  componentDidMount() {
    super.componentDidMount();

    if (this.props.mode === Mode.SETTINGS) {
      jQuery.getJSON(common_user_type_options_path(), (res) => {
        this.setState({user_type_options: res})
      });
    }
  }

  valueFormatter = (value) => {
    if (value == null) return null;
    if (typeof value === 'number') {
      return `${value}-user`;
    }
    const str = String(value);
    return str.includes('-user') ? str : null;
  }

  hasPrefilledAnswer(context) {
    const prefillAnswers = this.getPrefilledAnswer(context);
    return (prefillAnswers && Object.keys(prefillAnswers).length > 0);
  }

  renderView() {
    const {value = null, label = null} = this.getAnswer();
    const fieldName = this.fullFieldName();

    return (
      <div>
        {this.getLockedInformationTooltip(this.modeFillOrView() && this.isReadOnlyPrefilledAnswer(), <>
          <label className={this.props.content.label_hidden ? 'hidden-label' : ''}>
            <span className="field-label">{this.getText('label')}</span>
            {this.props.content.required && <span className='required-field'> *</span>}
            {this.helpIcon()}
            {this.renderPrefillIcon()}
          </label>

          {this.props.content.useOrganisationTreePicker ? (
            this.props.content.selectionMethod === 'modal' ?
              <UserOrganisationTreePicker
                name={`${fieldName}][value]`}
                value={this.valueFormatter(value)}
                label={label}
                placeholder={this.getText('placeholder')}
                onChange={(val) => this.answerChanged(val)}
                readOnly={!this.isFillMode()}
                disabled={(this.isFillMode() && this.isReadOnlyAnswer()) || this.props.disabled}
                excludeUserTypes={this.props.content.excludeUserTypes}
              /> :
              <UserTreeSelect
                name={`${fieldName}][value]`}
                value={this.valueFormatter(value)}
                label={label}
                placeholder={this.getText('placeholder')}
                onChange={(val) => this.answerChanged(val)}
                readOnly={!this.isFillMode()}
                disabled={(this.isFillMode() && this.isReadOnlyAnswer()) || this.props.disabled}
                excludeUserTypes={this.props.content.excludeUserTypes}
              />
            ) :
            <UserSelect
              name={`${fieldName}][value]`}
              value={this.valueFormatter(value)}
              label={label}
              valueFormatter={this.valueFormatter}
              placeholder={this.getText('placeholder')}
              onChange={(val) => this.answerChanged(val)}
              readOnly={!this.isFillMode()}
              disabled={(this.isFillMode() && this.isReadOnlyAnswer()) || this.props.disabled}
              excludeUserTypes={this.props.content.excludeUserTypes}
            />
          }

          {this.descriptionText()}
          {!this.modeFillOrView() && this.isReadOnlyAnswer() ? this.lockedInformationDefault() : ''}
        </>)}

        {this.isFillMode() && <input type='hidden' name={`${fieldName}][label]`} value={label}/>}
      </div>
    );
  }

  renderPrettyView() {
    const {hide = false, show_label = false} = this.props.content.prettyView || {};
    if (hide) return null;

    const {value = null, label = null} = this.getAnswer();

    return <div>
      {show_label &&
        <label>
          <span className="field-label">{this.getText('label')}</span>
        </label>
      }
      {value ? <ReadOnlyMultiSelectValues values={[{label: label}]}/> : <p><em>{I18n.t('no_selected_options', {scope: 'fe'})}</em></p>}
    </div>;
  }

  answerChanged = (value) => {
    this.updateAnswer(value);
    if (this.props.answersChangedCallback) {
      this.props.answersChangedCallback();
    }
  }

  renderSettings() {
    return (
      <div>
        <Setting.Checkbox name="required" label={I18n.t('field_required', {scope: 'fe'})} value={this.props.content.required} onChange={this.updateSettings}/>
        <Setting.TextField name="label" label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings}/>
        <Setting.Checkbox name="label_hidden" className="label-hidden-check" label={I18n.t('field_label_hide', {scope: 'fe'})} value={this.props.content.label_hidden} onChange={this.updateSettings}/>

        {!this.props.settings['hidePlaceholderField'] &&
          <Setting.TextField name="placeholder" label={I18n.t('field_placeholder', {scope: 'fe'})} value={this.getText('placeholder')} onChange={this.updateSettings}/>
        }
        {!this.props.settings['hideDescriptionField'] &&
          <Setting.LongTextField name="description" label={I18n.t('field_description', {scope: 'fe'})} value={this.getText('description')} onChange={this.updateSettings}/>
        }
        {!this.props.settings['hideHelpTextField'] &&
          <Setting.LongTextField name="help" label={I18n.t('field_help', {scope: 'fe'})} value={this.getText('help')} onChange={this.updateSettings}/>
        }

        <label className='w-100 mt-1'>{I18n.t('field_exclude_user_types', {scope: 'fe'})}</label>
        <Select
          name="excludeUserTypes"
          defaultValue={this.props.content.excludeUserTypes}
          options={this.state.user_type_options}
          onChange={(v) => this.updateSettingsSelection('excludeUserTypes', v)}
          isMulti={true}
          autoComplete={this.getAutoComplete()}
        />

        <Setting.Checkbox name="useOrganisationTreePicker" label={I18n.t('use_organisation_tree_picker', {scope: 'fe'})} value={this.props.content.useOrganisationTreePicker} onChange={this.updateSettings} />

        {this.props.content.useOrganisationTreePicker && 
          <Setting.RadioSelection
            name="selectionMethod"
            options={selectionMethods}
            label={I18n.t('field_selection_method', { scope: 'fe' })}
            selectedOption={this.props.content.selectionMethod||'dropdown'}
            onChange={this.updateSettings}
          />
        }

        <Setting.TextField
          name="reportKey"
          label={I18n.t('field_report_key', {scope: 'fe'})}
          value={this.props.content.reportKey}
          description={I18n.t('field_report_key_description', {scope: 'fe'})}
          {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
        />

        {this.renderPrettyViewSettings()}
      </div>
    );
  }

  updateSettingsSelection(fieldName, newValue) {
    this.props.updateSettingsCallback(
      this.props.content.id,
      update(this.props.content, {
        [fieldName]: { $set: newValue }
      })
    );
  }

  beforeUpdateSettings(content) {
    content.required = castDataType(content.required, DataType.BOOLEAN);
    content.label_hidden = castDataType(content.label_hidden, DataType.BOOLEAN);
    content.useOrganisationTreePicker = castDataType(content.useOrganisationTreePicker, DataType.BOOLEAN);
    content.excludeUserTypes = this.props.content.excludeUserTypes;
    this.beforeUpdateSettingsPrettyView(content);
  }
}
